import data from "./releaseLog.generated.json";

/**
 * The public release history behind /changelog, built from git by
 * `npm run changelog` (scripts/build-changelog.mjs). Nothing here is
 * hand-written: every line is a `vNN:` release commit subject off develop,
 * minus the deploy-plumbing commits that only describe a push.
 *
 * Named releaseLog, NOT changelog: `src/lib/changeLog.ts` is the in-app audit
 * trail, and on a case-insensitive filesystem a `changelog.ts` beside it
 * silently overwrites that file.
 *
 * The JSON is committed because the deployed container has no git — the
 * Docker build excludes `.git` and node:20-slim has no git binary — so the
 * history has to be baked in at authoring time.
 *
 * Consequence worth knowing: regeneration happens BEFORE the release commit
 * that ships it, so the newest entry on the live page is the previous
 * release, not the one currently serving. The page says so rather than
 * pretending otherwise.
 */

export type Release = {
  /** Image tag number: v262 → 262. */
  version: number;
  /** ISO date the release shipped (author date of its commit). */
  date: string;
  /** One line per release commit — several can share a version. */
  entries: string[];
  /** Filter tags derived from the text; a nav aid, not a taxonomy. */
  areas: string[];
};

export type ChangelogData = {
  generatedFrom: string;
  releaseCount: number;
  shippingDays: number;
  firstRelease: string;
  latestRelease: string;
  spanDays: number;
  releasesPerWeek: number;
  releasesPerShippingDay: number;
  releases: Release[];
};

export const CHANGELOG = data as ChangelogData;

/** Releases newest-first, already sorted by ship date then version. */
export const RELEASES: Release[] = CHANGELOG.releases;

/** Every area tag in use, most-used first — the filter chip row. */
export function areaCounts(): { area: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const r of RELEASES)
    for (const a of r.areas) counts.set(a, (counts.get(a) ?? 0) + 1);
  return [...counts.entries()]
    .map(([area, count]) => ({ area, count }))
    .sort((a, b) => b.count - a.count || a.area.localeCompare(b.area));
}

/** Releases grouped into the day they shipped, newest day first. */
export function releasesByDay(list: Release[] = RELEASES): {
  date: string;
  releases: Release[];
}[] {
  const days: { date: string; releases: Release[] }[] = [];
  for (const r of list) {
    const last = days[days.length - 1];
    if (last && last.date === r.date) last.releases.push(r);
    else days.push({ date: r.date, releases: [r] });
  }
  return days;
}

/** "10 August 2026" — the format the rest of the marketing site uses. */
export function formatDay(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return `${d} ${months[m - 1]} ${y}`;
}
